import type { TFunction } from 'i18next';

import { API_KEY_SCOPES, type TAPIKeyScope } from '@feathr/blackbox';

/** The wildcard grant level held by grandfathered keys — implies all scopes. */
const WILDCARD_SCOPE = '*';

/**
 * Whether a key's scopes amount to a full grant: the `*` wildcard or every
 * selectable scope. Exported so display logic can branch on the classification
 * itself instead of comparing summarizeScopes' translated output.
 */
export function isFullGrant(scopes: readonly TAPIKeyScope[] | readonly string[]): boolean {
  const scopeSet = new Set<string>(scopes);
  return scopeSet.has(WILDCARD_SCOPE) || API_KEY_SCOPES.every((scope) => scopeSet.has(scope));
}

/**
 * Summarizes a key's scopes for a table cell: "All" when the key holds the `*`
 * wildcard or every selectable scope, a bare count ("4", per design — the cell
 * gets a tooltip breaking the scopes down) for a subset, and an em dash for a
 * key with no scopes (e.g. legacy keys).
 */
export function summarizeScopes(
  scopes: readonly TAPIKeyScope[] | readonly string[] | undefined,
  t: TFunction,
): string {
  if (!scopes || scopes.length === 0) {
    return '—';
  }

  if (isFullGrant(scopes)) {
    return t('All');
  }

  return String(scopes.length);
}
